import { Vector3 } from "three";

import { JointPositions } from "../types";
import { SkeletalStructure } from "../types/statsApi";
import { Basis } from "../misc/util";

import { GeneralConstraint } from "./constraint/general";
import { Edge } from "./edge";
import { Group } from "./group";
import { PointEntry, PointType } from "./metadata";

const tempV = new Vector3();

export class Joint {
  name: string;

  group?: Group;

  parentEdge?: Edge;
  childEdges: Edge[] = [];

  pos = new Vector3(0, 0, 0);
  prevPos = new Vector3(0, 0, 0);

  posSet = false;
  basePos = new Vector3(0, 0, 0);
  initPos = new Vector3(0, 0, 0);

  props: PointEntry = { type: null };

  constructor(pos: Vector3, name: string) {
    this.name = name;
    // initialise geometry - all in world coords
    // TODO: why is this required?
    this.setInitialPos(pos);
  }

  getWeight(): number {
    switch (this.props.type) {
      case PointType.TARGETED:
        return this.props.weight ?? 1;
      case PointType.KABSCH:
        return 1;
      case PointType.FLOATING:
      default:
        return 0;
    }
  }

  setParentEdge(parent: Edge) {
    this.parentEdge = parent;
    if (!this.posSet) {
      this.setInitialPos(parent.start.basePos);
    }

    if (this.name == "not-set") {
      this.name = parent.name;
    }
  }

  private setInitialPos(pos: Vector3) {
    this.basePos.copy(pos);
    this.initPos.copy(pos);
    this.pos.copy(pos);

    this.posSet = true;
  }

  addChildEdge(edge: Edge): boolean {
    this.childEdges.push(edge);

    return true;
  }

  resolveCandidatePositions(): boolean {
    if (!this.isHub()) {
      console.error("can't resolve candidates for non-hub");
      return false;
    }

    this.pos.set(0, 0, 0);
    for (const edge of this.childEdges) {
      if (!edge.hasCandidateStartPos) {
        console.warn(`no valid candidate position for ${this.name}`);
        return false;
      }

      this.pos.add(edge.candidateStartPos);
      edge.candidateStartPosReset();
    }

    this.pos.divideScalar(this.childEdges.length);
    return true;
  }

  getBasies(basies: { [key: string]: Basis }) {
    for (const edge of this.childEdges) {
      basies[edge.name] = edge.basis;

      edge.end.getBasies(basies);
    }
  }

  getPoints(positions: JointPositions) {
    if (!this.name) {
      console.error("unamed joint");
      return;
    }
    positions.set(this.name, this.pos);

    for (const child of this.childEdges) {
      if (child.end) {
        child.end.getPoints(positions);
      }
    }
  }

  getStructure(structure: SkeletalStructure) {
    if (!this.name) {
      console.error("unamed joint");
      return;
    }
    structure[this.name] = [];
    for (const child of this.childEdges) {
      structure[this.name].push(child.name);
      child.end.getStructure(structure);
    }
  }

  getConstraints(constraints: GeneralConstraint[]) {
    if (this.parentEdge && this.parentEdge.hasConstraints()) {
      constraints.push(...this.parentEdge.constraints);
    }

    for (const child of this.childEdges) {
      if (child.hasConstraints()) {
        constraints.push(...child.constraints);
      }

      child.end.getConstraints(constraints);
    }
  }

  getJoints(joints: Joint[]) {
    joints.push(this);
    for (const edge of this.childEdges) {
      edge.end.getJoints(joints);
    }
  }

  getChildJoint(name: string) {
    const toVisit: Joint[] = [this];
    let current: Joint | undefined = undefined;
    let found: Joint | undefined = undefined;

    while ((current = toVisit.pop())) {
      if (current.name == name) {
        found = current;
        break;
      }

      current.childEdges.forEach((e) => toVisit.push(e.end));
    }

    return found;
  }

  getEdges(edges: Edge[]) {
    for (const child of this.childEdges) {
      edges.push(child);
      child.end.getEdges(edges);
    }
  }

  isHub(): boolean {
    const out =
      this.props.type != PointType.KABSCH && this.childEdges.length > 1;

    // console.log(this.name, "Hub", out);
    return out;
  }

  isRoot(): boolean {
    return (
      this.parentEdge === undefined || this.props.type == PointType.ROOT_BREAK
    );
  }

  isKabschParent(): boolean {
    const out =
      this.childEdges.length == 1
        ? this.childEdges[0].end.props.type == PointType.KABSCH
        : false;

    // console.log(this.name, "KabschParent", out);
    return out;
  }

  isKabschChild(): boolean {
    if (!this.parentEdge) {
      return false;
    }
    const out =
      this.parentEdge.start.props.type == PointType.KABSCH &&
      (!this.parentEdge.start.props.children ||
        this.parentEdge.start.props.children.includes(this.name));

    // console.log(this.name, "KabschChild", out);
    return out;
  }

  isLeafEffector(): boolean {
    let leaf =
      this.props.type == PointType.TARGETED &&
      !this.isHub() &&
      !this.isKabschChild();
    if (!leaf) {
      return false;
    }

    let current: Joint | undefined = this;

    while ((current = current.getNext())) {
      if (
        current.props.type != PointType.FLOATING ||
        current.childEdges.length > 1
      ) {
        leaf = false;
        break;
      }
    }

    // console.log(this.name, "leafEffector", leaf);

    return leaf;
  }

  isOrphaned(): boolean {
    const parentNode = this.parentEdge?.start;
    let orphan =
      parentNode != undefined &&
      (parentNode.isLeafEffector() || parentNode.isKabschChild()) &&
      parentNode.childEdges.length == 1;

    // console.log(this.name, "OrphanBase", out);

    if (orphan) {
      for (
        let current: Joint = this;
        current && orphan;
        current = current.childEdges[0]?.end
      ) {
        if (
          current.childEdges.length > 1 ||
          current.props.type != PointType.FLOATING
        ) {
          orphan = false;
        }
      }
    }

    return orphan;
  }

  moveBy(delta: Vector3) {
    this.pos.add(delta);

    for (const child of this.childEdges) {
      if (child.end) {
        child.end.moveBy(delta);
      }
    }
  }

  private getNext(): Joint | undefined {
    return this.childEdges.length == 1 ? this.childEdges[0].end : undefined;
  }
}
